import {
  createContext, FC, ReactNode, useCallback, useContext, useEffect, useMemo, useState,
} from 'react';

import { warmupFunction } from '../../../../hooks/useWarmup';

/**
 * Globalny stan asystenta AI „Stefan". Wcześniej panel otwierał się z pływającego
 * uchwytu (avatar przypięty do prawej krawędzi). Teraz — wzorem ElevenLabs —
 * otwiera go guzik „Zapytaj" w TopBarze, a otwarty panel dokuje się przy prawej
 * krawędzi (zwija sidebar i wpycha treść w lewo). Stan trzymamy w kontekście, bo
 * korzystają z niego trzy niezależne miejsca: TopBar (trigger), Sidebar
 * (force-collapse) i Layout/DockedHelpAiChat (dokowany panel + wcięcie treści).
 */
interface HelpAiChatContextValue {
  isOpen: boolean;
  /** Czy panel był kiedykolwiek otwarty (lazy-mount czatu). */
  hasOpened: boolean;
  open: () => void;
  close: () => void;
  toggle: () => void;
}

const noop = (): void => undefined;

const HelpAiChatContext = createContext<HelpAiChatContextValue>({
  isOpen: false,
  hasOpened: false,
  open: noop,
  close: noop,
  toggle: noop,
});

export const HelpAiChatProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  // Lazy mount: czat montujemy dopiero przy pierwszym otwarciu, potem zostaje
  // zamontowany (płynna animacja + zachowana historia rozmowy).
  const [hasOpened, setHasOpened] = useState(false);

  const open = useCallback(() => {
    setHasOpened(true);
    setIsOpen(true);
    // Rozgrzewamy instancję funkcji równolegle z animacją wysuwania panelu.
    warmupFunction('v3-panelHelpChat');
  }, []);

  const close = useCallback(() => setIsOpen(false), []);

  const toggle = useCallback(() => {
    setIsOpen((prev) => {
      const next = !prev;
      if (next) {
        setHasOpened(true);
        warmupFunction('v3-panelHelpChat');
      }
      return next;
    });
  }, []);

  // Esc zamyka panel.
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKeyDown = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen]);

  const value = useMemo<HelpAiChatContextValue>(
    () => ({
      isOpen, hasOpened, open, close, toggle,
    }),
    [isOpen, hasOpened, open, close, toggle],
  );

  return <HelpAiChatContext.Provider value={value}>{children}</HelpAiChatContext.Provider>;
};

export const useHelpAiChat = (): HelpAiChatContextValue => useContext(HelpAiChatContext);
